import React, { useEffect } from 'react';
import { Drawer } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useCommonAppContext } from '~/shared/contexts/CommonAppContext';
import { useIsAtOrNarrowerThan } from '~/shared/utils/useBreakpoint';
import { getLcn } from '~/utils/labeled-classnames';
import SuspendedArea from './SuspendedArea';
import MenuToggle from './MenuToggle';

export default function StandardSidebar({
  sideBarContent,
  sidebarBanner,
  pageUsesSidebar = true,
  useBgColor = true,
  heightOfBanner,
  heightOfFooterAndBanner,
  alwaysFloatSidebar = false,
}: {
  sideBarContent?: React.ReactElement
  sidebarBanner?: React.ReactElement
  pageUsesSidebar?: boolean
  useBgColor?: boolean
  heightOfBanner: number
  heightOfFooterAndBanner: number
  alwaysFloatSidebar?: boolean
}) {
  const isSmOrNarrower = useIsAtOrNarrowerThan('sm')
  const location = useLocation()
  const { sideBarOpen, setSideBarOpen } = useCommonAppContext();

  useEffect(() => {
    document.body.classList.toggle('left-sidebar-open', sideBarOpen);
  }, [sideBarOpen]);

  useEffect(() => {
    if (isSmOrNarrower && sideBarOpen) {
      setSideBarOpen(false);
    }
  }, [location]);

  const lcn = getLcn({
    standardSidebar: '',
    sideCol: [
      useBgColor && 'dark:bg-[#1b1b1b]',
      'w-[300px] transition-all',
      !sideBarOpen && '-ml-[300px] mt-4',
    ],
    sideColInner: 'w-[300px] md:sticky',
  });

  if (!pageUsesSidebar) return null;

  if (isSmOrNarrower || alwaysFloatSidebar) {
    return (
      <Drawer
        open={sideBarOpen}
        onClose={() => setSideBarOpen(false)}
        slotProps={{
          paper: {
            sx: {
              backgroundImage: 'none', //otherwise this is set to make it lighter
              backgroundColor: 'var(--scene-1)',
            }
          }
        }}
      >
        <div className='min-w-[200px] max-w-[300px]'>
          <SuspendedArea>
            <div className='flex gap-2 items-center justify-between'>
              {isSmOrNarrower &&
                <div className='p-2'>
                  {sidebarBanner}
                </div>
              }

              <div className='text-right'>
                <MenuToggle location='side' />
              </div>
            </div>
            {sideBarContent}
          </SuspendedArea>
        </div>
      </Drawer>
    );
  }

  return (
    <div className={lcn.sideCol}>
      <div className={lcn.sideColInner} style={{ top: `${heightOfBanner}px` }}>
        <nav style={{ maxHeight: `calc(100dvh - ${heightOfFooterAndBanner}px)`, overflowY: 'auto' }}>
          <SuspendedArea>
            {sideBarContent}
          </SuspendedArea>
        </nav>
      </div>
    </div>
  );
}
